import {
    Expression, FunctionExpression, BasicName,
    Identifier, Keyword, ColumnOperand, BasicOperand, IntegerLiteral,
    BinaryLiteral, DecimalLiteral, HexLiteral, OctLiteral, Literal, StringLiteral, FloatLiteral,
    FloatLiteralValue, HexfloatLiteral, MetaFunction, Assignment, VariableReference,
    ParameterReference, TemplateVariable, MetaVariable, MetaMatch, Scope, Or, BinaryOperator, Xor, And, Comparison,
    ComparisonOperatorType, EqNotEq, EqNotEqOperatorType, AddMinus, AddMinusType, MulDivMod,
    MulDivModOperatorType, BasicExpression, ColumnIdentifier, Configuration, ConfigName, UnaryMinus, Not,
    ArrayType,
    ArrayIndexer,
    FieldSelector,
    RegexLiteral,
    Struct,
    ParameterOperand,
    MetaArgument,
    Interpolation,
    MetaAssignment,
    TransientOperand,
    TransformFunction,
    Cast,
    Datatype,
    DecimalDatatype,
    ComplexDatatype,
    ArrayDatatype,
    ColumnDefinition,
    MapType,
    MapDatatype,
    ExternalDataType
} from '../models/expressionTypes';
import { IVisitorInterface } from '../models/IVisitorInterface';
import { Antlr4Utils } from '../models/antlr4-utils';
import { HIGHER_ORDER_FUNCTIONS } from '../constants';
import { Antlr4Types } from '../models/antlr4-types';

/* eslint-disable @typescript-eslint/no-explicit-any */
const dataflowParserVisitor = Antlr4Utils.getVisitorDefinition();

export class ExpressionVisitor extends (dataflowParserVisitor.DataflowParserVisitor as IVisitorInterface) {
    private _higherOrderFunctionNames = HIGHER_ORDER_FUNCTIONS;

    constructor(protected _tokenStream: any) {
        // eslint-disable-next-line constructor-super
        super();
    }

    public visit(context: any): any {
        if (!context) {
            return undefined;
        }
        const node = super.visit(context);
        if (node instanceof BasicExpression) {
            node.text = this._getText(context);
            this._getStartStop(node, context);
        }
        return node;
    }

    // Visit a parse tree produced by DataflowParser#CommentedExpression.
    public visitCommentedExpression(context: any): Expression {
        const expression = this.visit(context.expression());
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Struct.
    public visitStruct(context: any): Expression {
        const struct = new Struct();
        struct.assignments = this.visit(context.assignment());
        const expression = new Expression();
        expression.struct = struct;

        return expression;
    }

    // Visit a parse tree produced by DataflowParser#IntegerLiteral.
    public visitIntegerLiteral(context: any): IntegerLiteral {
        const integerLiteral = new IntegerLiteral();
        integerLiteral.value = this.visit(context.integer_literal());

        return integerLiteral;
    }

    // Visit a parse tree produced by DataflowParser#FloatLiteral.
    public visitFloatLiteral(context: any): FloatLiteral {
        const floatLiteral = new FloatLiteral();

        floatLiteral.value = this.visit(context.float_literal());

        return floatLiteral;
    }

    // Visit a parse tree produced by DataflowParser#StringLiteral.
    public visitStringLiteral(context: any): StringLiteral {
        const stringLiteral = new StringLiteral();
        stringLiteral.value = this._getText(context);
        return stringLiteral;
    }

    // Visit a parse tree produced by DataflowParser#RegexLiteral.
    public visitRegexLiteral(context: any): RegexLiteral {
        const regexLiteral = new RegexLiteral();
        regexLiteral.value = this._getText(context);
        return regexLiteral;
    }

    // Visit a parse tree produced by DataflowParser#integer_literal.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public visitInteger_literal(context: any): BinaryLiteral | DecimalLiteral | HexLiteral | OctLiteral {
        if (context.BINARY_LITERAL()) {
            const binaryLiteral = new BinaryLiteral();
            binaryLiteral.value = this._getText(context.BINARY_LITERAL());
            return binaryLiteral;
        } else if (context.DECIMAL_LITERAL()) {
            const decimalLiteral = new DecimalLiteral();
            decimalLiteral.value = this._getText(context.DECIMAL_LITERAL());
            return decimalLiteral;
        } else if (context.HEX_LITERAL()) {
            const hexLiteral = new HexLiteral();
            hexLiteral.value = this._getText(context.HEX_LITERAL());
            return hexLiteral;
        } else if (context.OCT_LITERAL()) {
            const octLiteral = new OctLiteral();
            octLiteral.value = this._getText(context.OCT_LITERAL());
            return octLiteral;
        }

        throw new Error('Handle Integer_literal type which is not handled');
    }

    // Visit a parse tree produced by DataflowParser#float_literal.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public visitFloat_literal(context: any): FloatLiteralValue | HexfloatLiteral {
        if (context.FLOAT_LITERAL()) {
            const floatLiteralValue = new FloatLiteralValue();
            floatLiteralValue.value = this._getText(context.FLOAT_LITERAL());
            return floatLiteralValue;
        } else if (context.HEX_FLOAT_LITERAL()) {
            const hexfloatLiteral = new HexfloatLiteral();
            hexfloatLiteral.value = this._getText(context.HEX_FLOAT_LITERAL());
            return hexfloatLiteral;
        }
        throw new Error('Handle float_literal type which is not handled');
    }

    // Visit a parse tree produced by DataflowParser#keyword.
    public visitKeyword(context: any): Keyword {
        const keyword: Keyword = new Keyword();
        keyword.name = this._getText(context);

        return keyword;
    }

    // Visit a parse tree produced by DataflowParser#basicName.
    public visitBasicName(context: any): BasicName {
        return this._visitBasicName(context, context.IDENTIFIER(), false);
    }

    // Visit a parse tree produced by DataflowParser#anyName.
    public visitAnyName(context: any): BasicName {
        return this._visitBasicName(context, context.ANY_IDENTIFIER(), true);
    }

    // Visit a parse tree produced by DataflowParser#columnIdentifier.
    public visitColumnIdentifier(context: any): ColumnIdentifier {
        const columnIdentifier = new ColumnIdentifier();
        if (context.basicName()) {
            columnIdentifier.name = this._getText(context);
        } else if (context.anyName()) {
            columnIdentifier.name = this._getText(context.anyName().ANY_IDENTIFIER() || context.anyName().keyWord());
        }

        return columnIdentifier;
    }

    // Visit a parse tree produced by DataflowParser#configName.
    public visitConfigName(context: any): ConfigName {
        const configName = new ConfigName();
        configName.name = this._getText(context);
        return configName;
    }

    // Visit a parse tree produced by DataflowParser#variableReference.
    public visitVariableReference(context: any): VariableReference {
        const variableReference = new VariableReference();
        variableReference.basicName = this.visit(context.basicName());

        return variableReference;
    }

    public visitParameterOperand(context: any): ParameterOperand {
        const parameterOperand = new ParameterOperand();
        parameterOperand.basicName = this.visit(context.basicName());
        return parameterOperand;
    }

    // Visit a parse tree produced by DataflowParser#parameterReference.
    public visitParameterReference(context: any): ParameterReference {
        const parameterReference = new ParameterReference();
        parameterReference.parameterOperand = this.visit(context.basicName());

        return parameterReference;
    }

    // Visit a parse tree produced by DataflowParser#function.
    public visitBasicFunction(context: any): FunctionExpression {
        const functionExpression: FunctionExpression = new FunctionExpression();
        functionExpression.basicName = this.visit(context.basicName());
        functionExpression.children = context.expression().map((expression: any) => {
            return this.visit(expression);
        });
        return functionExpression;
    }

    // Visit a parse tree produced by DataflowParser#configuration.
    public visitConfiguration(context: any): Configuration {
        const configuration = new Configuration();
        configuration.configName = this.visit(context.configName());
        configuration.expression = this.visit(context.expression());
        return configuration;
    }

    // Visit a parse tree produced by DataflowParser#metafunction.
    public visitMetafunction(context: any): MetaFunction {
        const metaFunction = new MetaFunction();
        metaFunction.basicName = this.visit(context.basicName());
        metaFunction.children = [];
        context.children.forEach((child: any) => {
            if (!(child instanceof Antlr4Types.BasicNameContextType || child instanceof Antlr4Types.TerminalNodeImplType)) {
                metaFunction.children.push(this.visit(child));
            }
        });

        return metaFunction;
    }

    // Visit a parse tree produced by DataflowParser#transformFunction.
    public visitTransformFunction(context: any): TransformFunction {
        const transformFunction = new TransformFunction();
        transformFunction.groupName = this._getText(context.groupName());
        transformFunction.transformName = this._getText(context.transformName());
        transformFunction.basicName = this.visit(context.basicName());
        if (!transformFunction.basicName.identifier) {
            transformFunction.basicName.identifier = transformFunction.basicName.keyword;
        }
        transformFunction.children = context.expression().map((expression: any) => {
            return this.visit(expression);
        });

        return transformFunction;
    }

    // Visit a parse tree produced by DataflowParser#Basic.
    public visitBasicSelector(context: any): BasicOperand {
        const basicOperand = new BasicOperand();
        basicOperand.basicName = this.visit(context.basicName());
        return basicOperand;
    }

    // Visit a parse tree produced by DataflowParser#Any.
    public visitAnySelector(context: any): BasicOperand {
        const basicOperand = new BasicOperand();
        basicOperand.basicName = this.visit(context.anyName());
        return basicOperand;
    }

    // Visit a parse tree produced by DataflowParser#columnOperand.
    public visitColumnOperand(context: any): ColumnOperand {
        const columnOperand = new ColumnOperand();
        columnOperand.name = this.visit(context.name());
        columnOperand.groupName = this._getText(context.groupName());
        columnOperand.transformName = this._getText(context.transformName());
        return columnOperand;
    }

    // Visit a parse tree produced by DataflowParser#MetaVariable.
    public visitMetaVariableReference(context: any): MetaVariable {
        const metaVariable = new MetaVariable();
        metaVariable.name = this._getText(context);
        return metaVariable;
    }
   // Visit a parse tree produced by DataflowParser#MetaPos
    public visitMetaPosReference(context: any): MetaVariable {
        return this.visitMetaVariableReference(context);
    }

    // Visit a parse tree produced by DataflowParser#MetaMatch.
    public visitMetaMatchReference(context: any): MetaMatch {
        const metaMatch = new MetaMatch();
        metaMatch.name = this._getText(context);
        return metaMatch;
    }

    // Visit a parse tree produced by DataflowParser#MetaVariable.
    public visitMetaArgumentReference(context: any): MetaVariable {
        const metaArgument = new MetaArgument();
        metaArgument.name = this._getText(context);
        let parentContext = context.parentCtx;
        const parentContextLineage = [parentContext];
        while (parentContext.parentCtx &&
                    (!parentContext.basicName || !this._higherOrderFunctionNames.includes(this._getText(parentContext.basicName()).toUpperCase())
                    || parentContextLineage.includes(parentContext.expression()[0]))) {
            parentContext = parentContext.parentCtx;
            parentContextLineage.push(parentContext);
        }
        metaArgument.arrayOrMapReference = parentContext && parentContext.expression &&
                                        parentContext.basicName && this._higherOrderFunctionNames.includes(this._getText(parentContext.basicName()).toUpperCase()) ?
                                        parentContext.expression()[0] : undefined;
        // Special case for accumulator reference in reduce function, which does not take the array type but instead takes the accumulator type
        if (metaArgument.name ===  '#acc' && this._getText(parentContext.basicName()) === 'reduce') {
            metaArgument.arrayOrMapReference = parentContext && parentContext.expression &&
                                        parentContext.basicName && this._higherOrderFunctionNames.includes(this._getText(parentContext.basicName()).toUpperCase())
                                        && parentContext.expression() && parentContext.expression()[1] ?
                                            parentContext.expression()[1] : undefined;
        }
        return metaArgument;
    }

    // Visit a parse tree produced by DataflowParser#AddMinus.
    public visitAddMinus(context: any): Expression {
        const addMinus = new AddMinus();
        addMinus.operation = this._getAddMinusOperatorType(context);
        this._visitBinaryOperator(context, addMinus);
        const expression = new Expression();
        expression.addMinus = addMinus;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#VariableRef.
    public visitVariableRef(context: any): VariableReference {
        const variableReference = new VariableReference();
        variableReference.basicName = this.visit(context.basicName());

        return variableReference;
    }

    // Visit a parse tree produced by DataflowParser#FunctionExpr.
    public visitFunctionExpr(context: any): Expression {
        const expression = new Expression();
        expression.functionExpression = this.visit(context.basicFunction());
        expression.functionExpression.isCompileTimeExecutable = false;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Array.
    public visitArray(context: any): Expression {
        const array = new ArrayType();
        array.children = this.visit(context.expression());
        const expression = new Expression();
        expression.array = array;
        this._getStartStop(expression, context);
        return expression;
    }

    public visitEmptyArray(context: any): Expression {
        const array = new ArrayType();
        array.children = [];
        const expression = new Expression();
        expression.array = array;
        this._getStartStop(expression, context);
        return expression;
    }

    public visitArrayIndexer(context: any): Expression {
        const arrayIndexer = new ArrayIndexer();
        this._visitBinaryOperator(context, arrayIndexer);
        const expression = new Expression();
        expression.arrayIndexer = arrayIndexer;
        return expression;
    }

    public visitMap(context: any): Expression {
        const map = new MapType();
        const expression = new Expression();
        map.children = this.visit(context.expression()) || [];
        expression.map = map;
        this._getStartStop(expression, context);

        return expression;
    }

    public visitFieldSelector(context: any): Expression {
        const fieldSelector = new FieldSelector();
        this._visitBinaryOperator(context, fieldSelector);
        const expression = new Expression();
        expression.fieldSelector = fieldSelector;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Or.
    public visitOr(context: any): Expression {
        const or = new Or();
        this._visitBinaryOperator(context, or);
        const expression = new Expression();
        expression.or = or;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#TemplateVar.
    public visitTemplateVar(context: any): TemplateVariable {
        const templateVariable = new TemplateVariable();
        templateVariable.basicName = this.visit(context.basicName());
        return templateVariable;
    }

    // Visit a parse tree produced by DataflowParser#UnaryMinus.
    public visitUnaryMinus(context: any): Expression {
        const unaryMinus = new UnaryMinus();
        unaryMinus.expression = this.visit(context.expression());
        const expression = new Expression();
        expression.unaryMinus = unaryMinus;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Not.
    public visitNot(context: any): Expression {
        const not = new Not();
        not.expression = this.visit(context.expression());
        const expression = new Expression();
        expression.not = not;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#MetaFunctionExpr.
    public visitMetaFunctionExpr(context: any): Expression {
        const expression = new Expression();
        expression.metafunction = this.visit(context.metafunction());

        return expression;
    }

    // Visit a parse tree produced by DataflowParser#transformFunctionExpr.
    public visitTransformFunctionExpr(context: any): Expression {
        const expression = new Expression();
        expression.transformFunction = this.visit(context.transformFunction());

        return expression;
    }

    // Visit a parse tree produced by DataflowParser#MulDivMod.
    public visitMulDivMod(context: any): Expression {
        const mulDivMod = new MulDivMod();
        mulDivMod.operation = this._getMulDivOperatorType(context);
        this._visitBinaryOperator(context, mulDivMod);
        const expression = new Expression();
        expression.mulDivMod = mulDivMod;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#ParameterRef.
    public visitParameterRef(context: any): ParameterReference {
        const parameterReference = new ParameterReference();
        parameterReference.parameterOperand = this.visit(context.parameterOperand());

        return parameterReference;
    }

    // Visit a parse tree produced by DataflowParser#EqNotEq.
    public visitEqNotEq(context: any): Expression {
        const eqNotEq = new EqNotEq();
        eqNotEq.operation = this._getEqNotEqOperatorType(context);
        this._visitBinaryOperator(context, eqNotEq);
        const expression = new Expression();
        expression.eqNotEq = eqNotEq;

        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Scope.
    public visitScope(context: any): Expression {
        const scope = new Scope();
        scope.expression = this.visit(context.expression());
        const expression = new Expression();
        expression.scope = scope;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Comparison.
    public visitComparison(context: any): Expression {
        const comparison = new Comparison();
        comparison.operation = this._getComparisonOperatorType(context);
        this._visitBinaryOperator(context, comparison);
        const expression = new Expression();
        expression.comparison = comparison;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#And.
    public visitAnd(context: any): Expression {
        const and = new And();
        this._visitBinaryOperator(context, and);
        const expression = new Expression();
        expression.and = and;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#LiteralExpr.
    public visitLiteralExpr(context: any): Expression {
        const literal = new Literal();
        literal.value = this.visit(context.literal());
        const expression = new Expression();
        expression.literal = literal;
        return expression;
    }

    public visitInterpolation(context: any): Expression {
        const interpolation = new Interpolation();
        interpolation.value = context.getText();
        interpolation.expressions = [];
        interpolation.strings = [];
        context.interpolateParts().forEach((interpolatePart: any) => {
            if (interpolatePart.INTERPOLATE_TEXT) {
                interpolation.strings.push(this.visitStringLiteral(interpolatePart));
            } else {
                interpolation.expressions.push(interpolatePart.expression());
            }
        });
        const expression = new Expression();
        expression.interpolation = interpolation;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Xor.
    public visitXor(context: any): Expression {
        const xor = new Xor();
        this._visitBinaryOperator(context, xor);
        const expression = new Expression();
        expression.xor = xor;
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#Cast.
    public visitCast(context: any): Expression {
        const cast = new Cast();
        cast.expression = this.visit(context.left);
        if (context.inlineDatatype()) {
            cast.inlineDatatype = this.visit(context.inlineDatatype());
        }
        const expression = new Expression();
        expression.cast = cast;

        return expression;
    }

    public visitColumnDefinition(context: any): ColumnDefinition {
        const columnDefinition = new ColumnDefinition();
        columnDefinition.name = this.visit(context.columnIdentifier());
        columnDefinition.datatype = new Datatype('string');
        if (context.inlineDatatype()) {
            columnDefinition.datatype = this.visit(context.inlineDatatype());
            if (context.inlineDatatype().dataFormat()) {
                let format = context.inlineDatatype().dataFormat().getText() as string;
                if (format.startsWith('[')) {
                    format = format.substring(1, format.length - 1);
                    format = format.indexOf(',') > -1 ? format.split(',')[0] : format;
                }
                columnDefinition.datatype.format = format.substring(1, format.length - 1);
            }
        } else if (context.externalDatatype()) {
            columnDefinition.datatype = new ExternalDataType();
            (columnDefinition.datatype as ExternalDataType).expression = this.visit(context.externalDatatype().expression());
        }
        return columnDefinition;
    }

    public visitInlineDatatype(context: any): Datatype {
        const datatypeContext = context.datatype();
        return this._visitDataTypeContext(datatypeContext);
    }

    public visitExternalDatatype(context: any): Datatype {
        const expression = new Expression();
        const external = new ExternalDataType();
        expression.parameterReference = this.visit(context.expression());
        external.expression = expression;
        return external;
    }

    // Visit a parse tree produced by DataflowParser#ColumnOperandRef.
    public visitColumnOperandRef(context: any): Expression {
        const expression = new Expression();
        expression.columnOperand = this.visit(context.columnOperand());
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#MetaVariable.
    public visitMetaVariableRef(context: any): Expression {
        const expression = new Expression();
        expression.metaVariable = this.visit(context.metaVariableReference());
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#MetaPos
    public visitMetaPosRef(context: any): Expression {
        const expression = new Expression();
        // For simplicity, make it such that $$ and $# are both assigned to metavariable
        expression.metaVariable = this.visit(context.metaPosReference());
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#MetaMatch.
    public visitMetaMatchRef(context: any): Expression {
        const expression = new Expression();
        expression.metaMatch = this.visit(context.metaMatchReference());
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#MetaVariable.
    public visitMetaArgumentRef(context: any): Expression {
        const expression = new Expression();
        expression.metaArgument = this.visit(context.metaArgumentReference());
        return expression;
    }

    public visitMetaAssignment(context: any): MetaAssignment {
        const assignment = new MetaAssignment();
        assignment.expression = this.visit(context.expression());
        return assignment;
    }

    // Visit a parse tree produced by DataflowParser#assignment.
    public visitAssignment(context: any): Assignment {
        const assignment = new Assignment();
        assignment.columnIdentifier = this.visit(context.columnIdentifier());
        assignment.expression = this.visit(context.expression());
        return assignment;
    }

    // Visit a parse tree produced by DataflowParser#templateVariable.
    public visitTemplateVariable(context: any): TemplateVariable {
        const templateVariable = new TemplateVariable();
        templateVariable.basicName = this.visit(context.basicName());
        return templateVariable;
    }

    public visitTransientRef(context: any): Expression {
        const expression = new Expression();
        expression.transientOperand = this.visit(context.transientOperand());
        return expression;
    }

    public visitTransientOperand(context: any): TransientOperand {
        const transientOperand = new TransientOperand();
        transientOperand.name = this.visit(context.name());
        return transientOperand;
    }

    // Visit a parse tree produced by DataflowParser#templateAssignment.
    public visitTemplateAssignment(context: any): void {
        // TODO - Implement this.
    }

    // Visit a parse tree produced by DataflowParser#eachFunction.
    public visitEachFunction(context: any): void {
        // TODO - Implement this.`
    }

    protected _getMulDivOperatorType(context: any): MulDivModOperatorType {
        if (context.MUL()) {
            return 'multiply';
        } else if (context.SLASH()) {
            return 'divide';
        } else if (context.MOD()) {
            return 'mod';
        }

        throw new Error(`Handle new type of MulDivMod operation ${this._getText(context.op)}`);
    }

    protected _getAddMinusOperatorType(context: any): AddMinusType {
        if (context.MINUS()) {
            return 'minus';
        } else if (context.PLUS()) {
            return 'plus';
        }

        throw new Error(`Handle new type of AddMinus operation ${this._getText(context.op)}`);
    }

    protected _getEqNotEqOperatorType(context: any): EqNotEqOperatorType {
        if (context.EQ()) {
            return 'eq';
        } else if (context.NOT_EQ()) {
            return 'notEq';
        } else if (context.EQ_NOCASE()) {
            return 'eqNoCase';
        } else if (context.EQ_NULL()) {
            return 'eqNull';
        }

        throw new Error(`Handle new type of EqNotEq operation ${this._getText(context.op)}`);
    }

    protected _getComparisonOperatorType(context: any): ComparisonOperatorType {
        if (context.GT()) {
            return 'gt';
        } else if (context.LT()) {
            return 'lt';
        } else if (context.GT_EQ()) {
            return 'gtEq';
        } else if (context.LT_EQ()) {
            return 'ltEq';
        }

        throw new Error(`Handle new type of comparision operation ${this._getText(context.op)}`);
    }

    private _visitBinaryOperator(context: any, binaryOperator: BinaryOperator): void {
        binaryOperator.leftExpression = this.visit(context.left);
        binaryOperator.rightExpression = this.visit(context.right);
    }

    private _getStartStop(expression: BasicExpression, context: any): void {
        // The context object contains the 'start' and 'stop' tokens of the expression.
        // The token object contains the column index in the code editor (which represents the start of the token, and, the token not necessarily has length 1)
        // To calculate start and stop, take the column position of the start token and calculate the last column position of the stop token

        if (context.start) {
            expression.start = context.start.start;
            expression.startPosition = { line: context.start.line, column: context.start.column };
        } else {
            expression.start = 0;
            expression.startPosition = { line: 0, column: 0 };
        }

        if (context.stop) {
            const stopTokenOffset = context.stop.text ? context.stop.text.length - 1 : 0;
            expression.stop = context.stop.stop;
            expression.stopPosition = { line: context.stop.line, column: context.stop.column + stopTokenOffset };
        } else {
            expression.stop = 0;
            expression.stopPosition = { line: 0, column: 0 };
        }
    }

    private _getText(node: any): string {
        return Antlr4Utils.getText(this._tokenStream, node);
    }

    private _visitBasicName(context: any, identifier: any, anyIdentifier: boolean): BasicName {
        const basicName: BasicName = new BasicName();

        if (identifier) {
            basicName.identifier = new Identifier();
            const identifierText = this._getText(identifier);
            // Removing {} from {Column Name with Spaces}.
            basicName.identifier.name = anyIdentifier
                ? identifierText.substring(1, identifierText.length - 1)
                : basicName.identifier.name = this._getText(identifier);
        }
        basicName.keyword = context.keyword() ? this.visit(context.keyword()) : undefined;

        return basicName;
    }

    private _visitDataTypeContext(datatypeContext: any): Datatype {
        let datatype: Datatype;
        if (datatypeContext.simple) {
            if (datatypeContext.simple().DECIMAL) {
                datatype = new DecimalDatatype();
                (datatype as DecimalDatatype).precision = parseInt(datatypeContext.simple().precision.getText(), 10);
                (datatype as DecimalDatatype).scale = parseInt(datatypeContext.simple().scale.getText(), 10);
            } else {
                datatype = new Datatype('string');
                datatype.datatype = datatypeContext.simple().getText();
            }
        } else if (datatypeContext.complex) {
            const complexDataFlowType = new ComplexDatatype();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            datatypeContext.complex().columnDefinition().forEach((nestedColumnDefinition: any) => {
                const nestedcolumn = this.visit(nestedColumnDefinition);
                complexDataFlowType.columnDefinitions.push(nestedcolumn);
            });
            datatype = complexDataFlowType;
        } else if (datatypeContext.OPEN_CLOSE_SQ) {
            const arraycolumn = new ArrayDatatype();
            arraycolumn.arrayType = this.visitInlineDatatype(datatypeContext);
            datatype = arraycolumn;
        } else if (datatypeContext.mapKey && datatypeContext.mapValue) {
            const mapColumn = new MapDatatype();
            mapColumn.keyType = this._visitDataTypeContext(datatypeContext.mapKey);
            mapColumn.valueType = this._visitDataTypeContext(datatypeContext.mapValue);
            datatype = mapColumn;
        }
        return datatype;
    }
}
